import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles, categories, difficultyColors } from '@/mocks/articles';
import ArticleTOC from './components/ArticleTOC';
import RelatedArticles from './components/RelatedArticles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-14 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-5">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link to="/category" className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i> 返回攻略列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.category);

  const tocItems = article.content
    .filter((block) => block.type === 'heading')
    .map((block, index) => ({
      id: `section-${index}`,
      level: block.level,
      text: block.text,
    }));

  const faqSchema = article.content
    .filter((b) => b.type === 'heading' && b.level === 2)
    .slice(0, 5)
    .map((heading) => {
      const headingIndex = article.content.indexOf(heading);
      const nextContent = article.content
        .slice(headingIndex + 1)
        .find((b) => b.type === 'paragraph');
      return {
        '@type': 'Question',
        name: heading.text,
        acceptedAnswer: {
          '@type': 'Answer',
          text: nextContent ? nextContent.text.slice(0, 200) : '查看详情',
        },
      };
    });

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 踩蘑菇攻略大全`}
        description={article.excerpt}
        keywords={`踩蘑菇,${article.title},踩蘑菇游戏攻略,${category ? category.name : '攻略'}`}
        canonicalUrl={`https://caimogu.example.com/article/${article.slug}`}
        ogType="article"
      />
      <SchemaOrg type="WebPage" data={{
        name: article.title,
        description: article.excerpt,
        url: `https://caimogu.example.com/article/${article.slug}`,
        inLanguage: 'zh-CN',
      }} />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: 'zh-CN',
        wordCount: article.content.reduce((acc, b) => acc + (b.text ? b.text.length : 0), 0),
      }} />
      {faqSchema.length > 0 && (
        <SchemaOrg type="FAQPage" data={{ mainEntity: faqSchema }} />
      )}
      <Navbar />
      <main className="pt-24 pb-14 md:pb-16 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '攻略分类', href: '/category' },
            ...(category ? [{ label: category.name, href: `/category?cat=${category.id}` }] : []),
            { label: article.title },
          ]} />
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
            <article className="flex-1 max-w-none lg:max-w-3xl">
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  {category && (
                    <Link
                      to={`/category?cat=${category.id}`}
                      className="bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${category.icon} mr-1`}></i>
                      {category.name}
                    </Link>
                  )}
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${difficultyColors[article.difficulty]}`}>
                    {article.difficulty}
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 leading-snug">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-xs text-foreground-400 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {article.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 阅读
                  </span>
                </div>
              </header>

              <div className="prose-custom">
                {article.content.map((block, index) => {
                  if (block.type === 'heading') {
                    const id = `section-${tocItems.findIndex((t) => t.text === block.text)}`;
                    if (block.level === 2) {
                      return (
                        <h2 key={index} id={id} className="text-xl font-heading text-foreground-900 mt-8 mb-4 pb-2 border-b border-background-200">
                          {block.text}
                        </h2>
                      );
                    }
                    return (
                      <h3 key={index} id={id} className="text-base font-semibold text-foreground-800 mt-6 mb-3">
                        {block.text}
                      </h3>
                    );
                  }
                  if (block.type === 'paragraph') {
                    return (
                      <p key={index} className="text-sm text-foreground-600 leading-relaxed mb-4">
                        {block.text}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>

              <RelatedArticles currentSlug={article.slug} category={article.category} />
            </article>

            <aside className="hidden lg:block w-56 flex-shrink-0">
              <ArticleTOC items={tocItems} />
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}